import type { DashboardSnapshot } from '@dash/shared';
import { Card } from '../components/Card.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { SectionHeader } from '../components/SectionHeader.tsx';
import { fmtInt, fmtPercent, fmtCurrency, fmtDuration } from '../lib/format.ts';

interface GeralProps {
  snap: DashboardSnapshot;
}

export function GeralView({ snap }: GeralProps) {
  const onlinePct = snap.rede.clientesTotal
    ? (snap.rede.clientesOnline / snap.rede.clientesTotal) * 100
    : 0;

  const onlineTone = onlinePct >= 95 ? 'ok' : onlinePct >= 90 ? 'warn' : 'crit';
  const filaTone = snap.atendimento.pabx.fila > 10 ? 'crit' : snap.atendimento.pabx.fila > 5 ? 'warn' : 'ok';
  const oltsTone = snap.rede.oltsAlerta > 0 ? 'crit' : 'ok';
  const abandonoTone = snap.atendimento.pabx.abandono > 15 ? 'crit' : snap.atendimento.pabx.abandono > 8 ? 'warn' : 'ok';
  const inadTone = snap.negocio.inadimplencia.percent > 8 ? 'crit' : snap.negocio.inadimplencia.percent > 5 ? 'warn' : 'ok';

  return (
    <div className="grid gap-4 p-6 lg:grid-cols-12">
      {/* HERO — Clientes Online (full-width, gigante) */}
      <div className="col-span-full">
        <div className={`card-base p-8 ${onlineTone === 'ok' ? 'card-glow-ok' : onlineTone === 'warn' ? 'card-glow-warn' : 'card-glow-crit'}`}>
          <span className={`tone-strip tone-strip-${onlineTone}`} aria-hidden />
          <div className="relative grid grid-cols-12 items-end gap-6">
            <div className="col-span-12 lg:col-span-6">
              <div className="eyebrow">Clientes online · rede FTTH</div>
              <div className={`mt-3 font-bold leading-none tracking-tight tabular-nums text-[--color-${onlineTone}] text-[7rem] lg:text-[9rem]`}>
                {fmtInt(snap.rede.clientesOnline)}
              </div>
              <div className="mt-3 flex items-baseline gap-3 text-[--color-fg-dim]">
                <span className={`text-2xl font-semibold tabular-nums text-[--color-${onlineTone}]`}>
                  {onlinePct.toFixed(1)}%
                </span>
                <span className="text-sm uppercase tracking-wider">de {fmtInt(snap.rede.clientesTotal)} contratos</span>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-6">
              <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-[--color-fg-muted]">
                <span>últimas 24h</span>
                <span>step 5min</span>
              </div>
              <Sparkline values={snap.rede.sparkline24h} height={120} />
            </div>
          </div>
        </div>
      </div>

      {/* SECTION — REDE */}
      <SectionHeader label="rede" hint="zabbix · OLTs · BGP" accent="info" />
      <div className="col-span-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card title="OLTs alerta" tone={oltsTone} value={fmtInt(snap.rede.oltsAlerta)} hint={`/ ${snap.rede.oltsTotal}`} size="lg" />
        <Card title="Zabbix crítico" tone={snap.rede.zabbixCriticos > 0 ? 'crit' : 'ok'} value={fmtInt(snap.rede.zabbixCriticos)} hint="severity 5" size="lg" />
        <Card title="BGP peers" tone={snap.rede.bgp.length === 0 ? 'neutral' : snap.rede.bgp.every(p => p.state === 'established') ? 'ok' : 'crit'} value={`${snap.rede.bgp.filter(p=>p.state==='established').length}/${snap.rede.bgp.length}`} hint="established" size="lg" />
        <Card title="Técnicos campo" tone="info" value={fmtInt(snap.atendimento.tecnicosCampo)} hint={`/ ${snap.atendimento.tecnicosTotal}`} size="lg" />
      </div>

      {/* SECTION — ATENDIMENTO */}
      <SectionHeader label="atendimento" hint="PABX · OS · campo" accent="accent" />
      <div className="col-span-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card title="Fila PABX" tone={filaTone} value={fmtInt(snap.atendimento.pabx.fila)} hint="aguardando" size="lg" />
        <Card title="TMA" tone="neutral" value={fmtDuration(snap.atendimento.pabx.tma)} hint="média móvel 50" size="lg" />
        <Card title="Atendidas hoje" tone="ok" value={fmtInt(snap.atendimento.pabx.atendidas)} hint="acumulado" size="lg" />
        <Card title="Abandono" tone={abandonoTone} value={fmtPercent(snap.atendimento.pabx.abandono)} hint="hoje" size="lg" />
      </div>
      <div className="col-span-12">
        <Card
          title="OS abertas"
          tone={snap.atendimento.osAbertas.total > 50 ? 'warn' : 'neutral'}
          value={fmtInt(snap.atendimento.osAbertas.total)}
          hint="total"
          size="lg"
          footer={
            <div className="flex flex-wrap gap-4 text-sm">
              <span><span className="text-[--color-fg-muted] uppercase tracking-wider text-[10px] mr-2">suporte</span><b className="text-[--color-fg] tabular-nums">{snap.atendimento.osAbertas.suporte}</b></span>
              <span><span className="text-[--color-fg-muted] uppercase tracking-wider text-[10px] mr-2">instalação</span><b className="text-[--color-fg] tabular-nums">{snap.atendimento.osAbertas.instalacao}</b></span>
              <span><span className="text-[--color-fg-muted] uppercase tracking-wider text-[10px] mr-2">manutenção</span><b className="text-[--color-fg] tabular-nums">{snap.atendimento.osAbertas.manutencao}</b></span>
            </div>
          }
        />
      </div>

      {/* SECTION — NEGÓCIO */}
      <SectionHeader label="negócio" hint="vendas · churn · financeiro" accent="ok" />
      <div className="col-span-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card title="Vendas hoje" tone="ok" value={fmtInt(snap.negocio.vendasDia)} hint="dia atual" size="lg" />
        <Card title="Vendas mês" tone="ok" value={fmtInt(snap.negocio.vendasMes)} hint={`meta ${fmtInt(snap.negocio.metaMes)}`} size="lg" />
        <Card title="Cancelamentos" tone={snap.negocio.cancelamentos > 20 ? 'warn' : 'neutral'} value={fmtInt(snap.negocio.cancelamentos)} hint="mês" size="lg" />
        <Card title="Inadimplência" tone={inadTone} value={fmtPercent(snap.negocio.inadimplencia.percent)} hint={fmtCurrency(snap.negocio.inadimplencia.valor)} size="lg" />
      </div>
    </div>
  );
}
